import React, { useEffect, useState } from 'react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import {
  createPasskeyAuthenticationOptions,
  fetchPasskeyStatus,
  verifyPasskeyAuthentication,
} from '../api';

export default function LoginPage({ onLogin }) {
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('Andreas');
  const [setupToken, setSetupToken] = useState(() => window.location.pathname === '/zugang' ? window.location.hash.slice(1) : '');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [setupDone, setSetupDone] = useState(false);
  useEffect(() => {
    if (setupToken) window.history.replaceState({}, '', '/zugang');
  }, [setupToken]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyStatus, setPasskeyStatus] = useState(null);

  useEffect(() => {
    fetchPasskeyStatus()
      .then(setPasskeyStatus)
      .catch(() => setPasskeyStatus({ enabled: false, registered: false }));
  }, []);

  async function handlePasskeyLogin() {
    setError('');
    setPasskeyLoading(true);
    try {
      const optionsJSON = await createPasskeyAuthenticationOptions();
      const response = await startAuthentication({ optionsJSON });
      await verifyPasskeyAuthentication(response);
      onLogin();
    } catch (passkeyError) {
      if (passkeyError && (passkeyError.name === 'NotAllowedError' || passkeyError.code === 'ERROR_CEREMONY_ABORTED')) {
        setError('Die Anmeldung wurde abgebrochen oder nicht bestätigt.');
      } else {
        setError(passkeyError.message || 'Passkey-Anmeldung fehlgeschlagen.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (setupToken) {
        if (password !== repeatPassword) throw new Error('Die Passwörter stimmen nicht überein.');
        if (password.length < 12 || password.length > 128) throw new Error('Bitte ein Passwort mit 12 bis 128 Zeichen wählen.');
        const response = await fetch('/api/account/activate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: setupToken, password }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Einrichtung fehlgeschlagen');
        setUsername(data.username);
        setSetupToken('');
        setPassword('');
        setRepeatPassword('');
        setSetupDone(true);
        window.history.replaceState({}, '', '/');
        return;
      }
      const r = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (r.ok) {
        onLogin();
      } else {
        const data = await r.json();
        setError(data.error || 'Passwort falsch');
      }
    } catch (error) {
      setError(error.message || 'Server nicht erreichbar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand-mark" aria-hidden="true">L</div>
        <div className="login-kicker">Dachboden</div>
        <h1 className="login-title">Lagerverwaltung</h1>
        <p className="login-copy">Boxen finden, Inhalte dokumentieren und Lagerplätze im Blick behalten.</p>
        {!setupToken && passkeyStatus?.enabled && passkeyStatus?.registered && browserSupportsWebAuthn() && (
          <>
            <button
              className="login-passkey-button"
              type="button"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading || loading}
            >
              <span className="login-passkey-symbol" aria-hidden="true">◎</span>
              <span>{passkeyLoading ? 'Gerät wird geprüft …' : 'Mit Face ID oder Passkey anmelden'}</span>
            </button>
            <div className="login-divider"><span>oder mit Passwort</span></div>
          </>
        )}
        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {setupToken ? <p>Eigenen Zugang aktivieren: Lege ein Passwort mit mindestens 12 Zeichen fest.</p> : (
            <label>
              Anmeldename
              <input className="login-input" type="text" name="username" autoComplete="username webauthn"
                value={username} onChange={event => setUsername(event.target.value)} required />
            </label>
          )}
          {setupDone && <p role="status">Dein Zugang ist eingerichtet. Melde dich jetzt mit deinem Anmeldenamen und Passwort an.</p>}
          <input
            className="login-input"
            type="password"
            name="password"
            placeholder="Passwort"
            autoComplete={setupToken ? "new-password" : "current-password"}
            aria-label={setupToken ? "Neues Passwort" : "Passwort"}
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
          />
          {setupToken && <input className="login-input" type="password" name="password-repeat"
            autoComplete="new-password" placeholder="Passwort wiederholen" aria-label="Passwort wiederholen"
            value={repeatPassword} onChange={event => setRepeatPassword(event.target.value)} />}
          {error && <div className="login-error">{error}</div>}
          <button className="login-button" type="submit" disabled={loading}>
            {loading ? 'Bitte warten…' : setupToken ? 'Zugang aktivieren' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
}
